/**
 * Admin-only web access policy.
 *
 * Until per-profile permissions ("who sees what") are defined, the desktop web
 * app is restricted to admin-level profiles. The field app (/field/*) is
 * intentionally exempt and keeps its own unrestricted login.
 *
 * Enforced in two places that share this helper:
 *  - slices/auth/otp/thunk.ts  → blocks non-admins at web login (clear message).
 *  - Routes/AuthProtected.tsx  → backstops desktop routes for pre-existing or
 *    field-issued sessions (the field app and web app share one token).
 */

// Profile names exactly as seeded in the backend (DbProfileSeed): the tenant
// "Admin" plus the system-level profiles, so the owner/super-admin/support
// accounts can never be locked out of their own environment.
export const ADMIN_WEB_ALLOWED_PROFILES = [
  'Admin',
  'SuperAdmin',
  'AppOwner',
  'SupportStaff',
] as const;

/**
 * True if a profile name is permitted to use the desktop web app.
 *
 * Comparison is case-insensitive. Fail-open on a missing/empty profile name:
 * the login thunk is the authoritative gate and always receives a profileName,
 * so an empty value here only happens for unexpected/legacy state — we don't
 * want to lock a legitimate admin out over it.
 */
export function isAllowedWebProfile(profileName?: string | null): boolean {
  const name = (profileName ?? '').trim().toLowerCase();
  if (!name) return true;
  return ADMIN_WEB_ALLOWED_PROFILES.some((p) => p.toLowerCase() === name);
}
